import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Package, 
  Plus, 
  Minus, 
  Check,
  Loader2,
  Search,
  ScanBarcode,
  Camera
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BarcodeScanner from '../components/BarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SaleRecordPage = ({ diagnosis, onBack, onComplete }) => {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductToggle = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.product_id === product.id);
      if (exists) {
        return prev.filter(p => p.product_id !== product.id);
      } else {
        return [...prev, {
          product_id: product.id,
          name: product.name,
          quantity: 1,
          price: product.price || 0
        }];
      }
    });
  };

  const handleQuantityChange = (productId, delta) => {
    setSelectedProducts(prev => 
      prev.map(p => {
        if (p.product_id === productId) {
          const newQty = Math.max(1, p.quantity + delta);
          return { ...p, quantity: newQty };
        }
        return p;
      })
    );
  };

  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    
    // Search product by barcode or name
    const product = products.find(p => 
      p.id === barcodeInput || 
      (p.name || '').toLowerCase().includes(barcodeInput.toLowerCase())
    );
    
    if (product) {
      handleProductToggle(product);
      toast.success(`${product.name} ${language === 'fr' ? 'ajouté' : 'added'}`);
    } else {
      toast.error(language === 'fr' ? 'Produit non trouvé' : 'Product not found');
    }
    setBarcodeInput('');
  };

  const handleBarcodeScan = (code) => {
    setShowScanner(false);
    // Search product by scanned barcode (could be ID or product code)
    const product = products.find(p => 
      p.id === code || 
      (p.name || '').toLowerCase().includes(code.toLowerCase()) ||
      (p.barcode && p.barcode === code)
    );
    
    if (product) {
      // Check if already in cart
      const existingItem = selectedProducts.find(sp => sp.product_id === product.id);
      if (existingItem) {
        handleQuantityChange(product.id, 1);
        toast.success(`${product.name} +1`);
      } else {
        handleProductToggle(product);
        toast.success(`${product.name} ${language === 'fr' ? 'ajouté' : 'added'}`);
      }
    } else {
      toast.error(language === 'fr' ? `Code non trouvé: ${code}` : `Code not found: ${code}`);
    }
  };

  const totalAmount = selectedProducts.reduce(
    (sum, p) => sum + (p.price * p.quantity), 
    0
  );

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast.error(language === 'fr' ? 'Sélectionnez au moins un produit' : 'Select at least one product');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/sales`, {
        diagnosis_id: diagnosis.id,
        products: selectedProducts,
        client_name: diagnosis.client_name,
        total_amount: totalAmount
      });
      
      toast.success(language === 'fr' ? 'Vente enregistrée!' : 'Sale recorded!');
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = products.filter(p => 
    (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.main_ingredient || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort by diagnosis concern
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (diagnosis?.main_concern) {
      const aMatch = a.target_concerns?.includes(diagnosis.main_concern) ? -1 : 0;
      const bMatch = b.target_concerns?.includes(diagnosis.main_concern) ? -1 : 0;
      return aMatch - bMatch;
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-40 sm:pb-44">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-[#78716C] hover:text-[#0F172A] transition-colors"
              data-testid="sale-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-[#0F172A]">
                {language === 'fr' ? 'Enregistrer une vente' : 'Record Sale'}
              </h1>
              <p className="text-sm text-[#78716C]">
                {diagnosis.client_name || 'Client'}
              </p>
            </div>
          </div>
          <Badge className="bg-[#E0F2FE] text-[#0C4A6E]">
            Score: {diagnosis.skin_score}
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Barcode Scanner Input */}
        <Card className="border-[#E7E5E4]">
          <CardContent className="p-4">
            <form onSubmit={handleBarcodeSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                <Input
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  placeholder={language === 'fr' ? 'Code-barres ou nom du produit...' : 'Barcode or product name...'}
                  className="pl-10"
                  data-testid="barcode-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="barcode-submit">
                <Plus className="w-4 h-4" />
              </Button>
              <Button 
                type="button" 
                onClick={() => setShowScanner(true)}
                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
                data-testid="open-scanner-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Scanner' : 'Scan'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'fr' ? 'Rechercher un produit...' : 'Search product...'}
            className="pl-12"
            data-testid="product-search"
          />
        </div>

        {/* Selected Products Summary */}
        {selectedProducts.length > 0 && (
          <Card className="border-[#0EA5E9] bg-[#E0F2FE]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-[#0F172A]">
                  {language === 'fr' ? 'Panier' : 'Cart'} ({selectedProducts.length})
                </h3>
                <span className="text-lg font-bold text-[#0EA5E9]">
                  {totalAmount.toLocaleString('fr-FR')} FCFA
                </span>
              </div>
              <div className="space-y-2">
                {selectedProducts.map(item => (
                  <div key={item.product_id} className="flex items-center justify-between text-sm">
                    <span className="text-[#0F172A] truncate flex-1">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleQuantityChange(item.product_id, -1)}
                        className="w-6 h-6 rounded bg-white flex items-center justify-center"
                        data-testid={`qty-minus-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-6 text-center">{item.quantity}</span>
                      <button 
                        onClick={() => handleQuantityChange(item.product_id, 1)}
                        className="w-6 h-6 rounded bg-white flex items-center justify-center"
                        data-testid={`qty-plus-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <span className="w-16 text-right text-[#78716C]">
                        {(item.price * item.quantity).toLocaleString('fr-FR')} FCFA
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products List */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-[#78716C] uppercase tracking-wider">
            {language === 'fr' ? 'Produits recommandés' : 'Recommended Products'}
          </h3>
          
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0EA5E9]" />
            </div>
          ) : (
            <div className="grid gap-3">
              {sortedProducts.map(product => {
                const isSelected = selectedProducts.some(p => p.product_id === product.id);
                const isRecommended = diagnosis?.main_concern && 
                  product.target_concerns?.includes(diagnosis.main_concern);
                
                return (
                  <Card 
                    key={product.id}
                    className={`border transition-all cursor-pointer ${
                      isSelected 
                        ? 'border-[#0EA5E9] bg-[#E0F2FE]/20' 
                        : 'border-[#E7E5E4] hover:border-[#BAE6FD]'
                    }`}
                    onClick={() => handleProductToggle(product)}
                    data-testid={`sale-product-${product.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected 
                          ? 'bg-[#0EA5E9] border-[#0EA5E9]' 
                          : 'border-[#E7E5E4]'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      
                      <div className="w-12 h-12 rounded-lg bg-[#F5F5F4] overflow-hidden flex-shrink-0">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-[#E7E5E4]" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#0F172A] truncate">{product.name}</p>
                          {isRecommended && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {language === 'fr' ? 'Reco' : 'Reco'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-[#78716C]">{product.main_ingredient}</p>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-semibold text-[#0F172A]">
                          {product.price?.toLocaleString('fr-FR') || '0'} FCFA
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Fixed Bottom Bar - Above bottom nav */}
      <div className="fixed bottom-[calc(70px+env(safe-area-inset-bottom,0px))] sm:bottom-[70px] left-0 right-0 bg-white border-t border-[#E7E5E4] p-4 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-[#78716C]">
              {language === 'fr' ? 'Total' : 'Total'}
            </p>
            <p className="text-2xl font-bold text-[#0F172A]">
              {totalAmount.toLocaleString('fr-FR')} FCFA
            </p>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={selectedProducts.length === 0 || submitting}
            className="flex-1 max-w-xs bg-[#0F172A] hover:bg-[#334155] text-white h-12 rounded-full"
            data-testid="confirm-sale-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ShoppingCart className="w-5 h-5 mr-2" />
                {language === 'fr' ? 'Valider la vente' : 'Confirm Sale'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
          language={language}
        />
      )}
    </div>
  );
};

export default SaleRecordPage;
